import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../services/api';
import LogoHeader from '../components/LogoHeader';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Capturer le paramètre tab de l'URL pour rediriger après connexion
  const redirectTab = searchParams.get('tab');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success('Connexion réussie!');

      // Construire l'URL de redirection avec le paramètre tab si présent
      const tabSuffix = redirectTab ? `?tab=${redirectTab}` : '';

      switch (user.role) {
        case 'admin':
          navigate('/admin');
          break;
        case 'referent':
          navigate('/referent');
          break;
        case 'animateur':
          navigate('/animateur');
          break;
        case 'financeur':
          navigate('/financeur');
          break;
        case 'bénéficiaire':
          navigate(`/bénéficiaire${tabSuffix}`);
          break;
        default:
          navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }

    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la demande');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header avec logos partenaires */}
      <LogoHeader />

      <div className="flex-1 flex">
        <div className="flex-1 flex items-center justify-center bg-white p-4 sm:p-6 md:p-8">
          <div className="w-full max-w-md">
            <div className="mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Plateforme d'Insertion
              </h1>
              <p className="text-slate-600 text-sm sm:text-base" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                Connectez-vous pour accéder à votre espace
              </p>
            </div>

            <Card className="border-slate-200 shadow-sm rounded-2xl sm:rounded-3xl">
              <CardHeader className="space-y-1">
                <CardTitle className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Connexion
                </CardTitle>
                <CardDescription style={{ fontFamily: 'DM Sans, sans-serif' }}>
                  Saisissez vos identifiants
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      Email
                    </Label>
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="votre.email@exemple.fr"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      Mot de passe
                    </Label>
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="rounded-xl bg-slate-50 border-slate-200 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6 py-3 font-bold"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {loading ? 'Connexion...' : 'Se connecter'}
                  </Button>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        <div
          className="hidden lg:block flex-1 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1744749903906-08490b29ae7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNvbG9yZnVsJTIwbW9kZXJuJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzQ0NzU5ODd8MA&ixlib=rb-4.1.0&q=85')`
          }}
        />
      </div>

      {/* Dialog Mot de passe oublié */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => { setShowForgotPassword(open); if (!open) { setForgotSent(false); setForgotEmail(''); } }}>
        <DialogContent className="sm:max-w-md rounded-2xl sm:rounded-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>Mot de passe oublié</DialogTitle>
            {!forgotSent && (
              <DialogDescription>
                Saisissez votre adresse email. Un lien sécurisé pour définir un nouveau mot de passe vous sera envoyé par email (valable 24 h).
              </DialogDescription>
            )}
          </DialogHeader>

          {forgotSent ? (
            /* ─── État : email envoyé ─── */
            <div className="py-6 space-y-4">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-2xl">✉️</span>
                </div>
                <p className="text-slate-700 text-sm leading-relaxed">
                  Si l'adresse <strong>{forgotEmail}</strong> correspond à un compte, un email avec le lien de réinitialisation vient d'être envoyé.
                </p>
                <p className="text-slate-500 text-xs">
                  Vérifiez également vos spams. Le lien est valable 24 heures.
                </p>
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <Button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  variant="outline"
                  className="rounded-xl border-blue-200 text-blue-600 hover:bg-blue-50"
                >
                  {forgotLoading ? 'Renvoi en cours...' : '🔄 Renvoyer l\'email'}
                </Button>
                <Button
                  onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                  className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                >
                  Fermer
                </Button>
              </div>
            </div>
          ) : (
            /* ─── État : formulaire ─── */
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Adresse email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="votre.email@exemple.fr"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
                    className="rounded-xl"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowForgotPassword(false)} className="rounded-xl">
                  Annuler
                </Button>
                <Button
                  onClick={handleForgotPassword}
                  disabled={forgotLoading}
                  className="bg-blue-600 hover:bg-blue-700 rounded-xl"
                >
                  {forgotLoading ? 'Envoi...' : 'Envoyer le lien'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
